import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { BaseError } from 'helpers/BaseError';

import alternateKeys from 'hooks/alternates/_queryKeys';
import formulaKeys from 'hooks/formulas/_queryKeys';
import useGetCurrentMessageContent from 'hooks/message-content/useGetCurrentMessageContent';
import personalizationKeys from 'hooks/personalization/_queryKeys';

import { VariantWithLabel } from 'models/variant.model';

import API from '../../services/api';

import messageContentKeys from '../message-content/_queryKey';

const useVariantDelete = (componentId: string, formulaId: number) => {
    const queryClient = useQueryClient();
    const { currentMessageContentId } = useGetCurrentMessageContent(formulaId);
    const queryKey = alternateKeys.getVariantsByComponentId(componentId);

    return useMutation<AxiosResponse<string>, BaseError, string>({
        mutationFn: (id: string) => API.deleteVariant(id),
        onMutate: async (deletedAlternateId) => {
            const previousAlternates: VariantWithLabel[] =
                (await queryClient.getQueryData(queryKey)) || [];
            queryClient.setQueryData(queryKey, () =>
                previousAlternates.filter(
                    (alt: VariantWithLabel) => alt.id !== deletedAlternateId,
                ),
            );
            return { previousAlternates };
        },
        onError: (err, newAlternate, context: any) => {
            queryClient.setQueryData(queryKey, context.previousAlternates);
        },
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: alternateKeys.getVariantsByMessageContentId(
                    currentMessageContentId as string,
                ),
            });
            queryClient.invalidateQueries({
                queryKey: alternateKeys.getVariantsByComponentId(componentId),
            });
            queryClient.invalidateQueries({
                queryKey: queryKey,
            });
            queryClient.invalidateQueries({
                queryKey: formulaKeys.formulaMessages(
                    formulaId,
                    currentMessageContentId,
                ),
            });
            queryClient.invalidateQueries({
                queryKey: personalizationKeys.personalizationMap,
            });
            queryClient.invalidateQueries({
                queryKey: personalizationKeys.getHeroRecommendations(),
            });

            queryClient.invalidateQueries({
                queryKey: formulaKeys.formulaMessageCountUpdate(formulaId),
            });

            if (currentMessageContentId) {
                queryClient.invalidateQueries({
                    queryKey: messageContentKeys.messageContent(
                        currentMessageContentId,
                    ),
                });
            }
        },
    });
};

export default useVariantDelete;
